import { getStorage } from 'firebase/storage'
import type { FirebaseApp } from 'firebase/app'
import { initializeApp } from 'firebase/app'
import type { Firestore } from 'firebase/firestore'
import {
  addDoc as addDocument,
  collectionGroup as collectionGroupRef,
  collection as collectionRef,
  doc as docRef,
  getDoc,
  initializeFirestore,
  serverTimestamp,
  setDoc as setDocument,
} from 'firebase/firestore'

import type { FirestoreType } from '~/models/firestoreType'
import type { Identifiable } from '~/models/identifiable'
import type { Functions } from 'firebase/functions'
import { getFunctions } from 'firebase/functions'

const firebaseGlobals = {
  app: null as FirebaseApp | null,
  db: null as Firestore | null,
  functions: null as Functions | null,
}

export function useFirebaseConfig() {
  const { public: config } = useRuntimeConfig()

  return {
    apiKey: config.FB_API_KEY,
    authDomain: `${config.FB_PROJECT_ID}.firebaseapp.com`,
    databaseURL: `https://${config.FB_PROJECT_ID}.firebaseio.com`,
    projectId: config.FB_PROJECT_ID,
    storageBucket: `${config.FB_PROJECT_ID}.appspot.com`,
    messagingSenderId: config.FB_MESSAGING_SENDER_ID,
    appId: config.FB_APP_ID,
    measurementId: config.FB_MEASUREMENT_ID,
  }
}

export function useFirebaseApp() {
  firebaseGlobals.app ??= initializeApp(useFirebaseConfig())
  return firebaseGlobals.app
}

export default function useFirebase() {
  const config = useRuntimeConfig()
  const { reportError } = useSentry()

  const firebaseApp = useFirebaseApp()

  firebaseGlobals.db ??= initializeFirestore(firebaseApp, {})

  const db = firebaseGlobals.db
  firebaseGlobals.functions ??= getFunctions(firebaseApp, 'europe-west3')
  const functions = firebaseGlobals.functions

  const storageLogistic = getStorage(
    firebaseApp,
    config.public.GC_STORAGE_LOGISTIC,
  )
  const storageApro = getStorage(firebaseApp, config.public.GC_STORAGE_APRO)

  const storagePrescriptionAttachments = getStorage(
    firebaseApp,
    config.public.GC_MEDAPP_PRESCRIPTION_STORAGE,
  )

  // Generic converter
  const firestoreGenericConverter = <T extends Identifiable>() =>
    ({
      toFirestore(data) {
        const { ...doc } = data
        return doc
      },
      fromFirestore(snapshot): T {
        return {
          ...snapshot.data(),
          id: snapshot.id,
          ref: snapshot.ref,
        } as T
      },
    }) as FirestoreDataConverter<T>

  const doc = <T extends FirestoreType>(
    collectionName: string,
    ...pathSegments: string[]
  ) =>
    docRef(db, collectionName, ...pathSegments).withConverter(
      firestoreGenericConverter<T>(),
    )

  const collection = <T extends FirestoreType>(
    collectionName: string,
    ...pathSegments: string[]
  ) =>
    collectionRef(db, collectionName, ...pathSegments).withConverter(
      firestoreGenericConverter<T>(),
    )

  const collectionGroup = <T extends FirestoreType>(collectionName: string) =>
    collectionGroupRef(db, collectionName).withConverter(
      firestoreGenericConverter<T>(),
    )

  const addDoc = <T extends FirestoreType>(
    reference: CollectionReference<T>,
    data: Omit<WithFieldValue<T>, keyof FirestoreType>,
  ) => {
    return addDocument(reference, {
      ...data,
      Created: serverTimestamp(),
      Updated: serverTimestamp(),
    } as WithFieldValue<T>)
  }

  const setDoc = <T extends FirestoreType>(
    reference: DocumentReference<T, DocumentData>,
    data: Omit<WithFieldValue<T>, keyof FirestoreType>,
  ) => {
    return setDocument(reference, data as WithFieldValue<T>)
  }

  const getDocument = async <T extends Identifiable>(path: string) => {
    const documentSnaphot = await getDoc(
      doc(path).withConverter(firestoreGenericConverter<T>()),
    )
    if (!documentSnaphot.exists()) {
      reportError('Document does not exist', { path })
      return
    }
    return documentSnaphot.data()
  }

  return {
    addDoc,
    addDocument,
    setDoc,
    collection,
    collectionGroup,
    collectionRef,
    db,
    doc,
    docRef,
    firestoreGenericConverter,
    getDocument,
    storageApro,
    storageLogistic,
    storagePrescriptionAttachments,
    functions,
  }
}
